/**
 * Puente único Staking ↔ Polygon (contrato + opcional indexer).
 *
 * Cuando el contrato esté listo:
 * 1. Añade en `.env`: REACT_APP_STAKING_CONTRACT_ADDRESS=0x...
 *    Opcional: REACT_APP_STAKING_RPC_URL (Polygon RPC si no usas el provider de la wallet).
 * 2. Coloca el ABI (p. ej. `src/services/staking/stakingContractAbi.json`) e impórtalo aquí.
 * 3. Implementa las funciones marcadas con `IMPLEMENTAR` usando `ethers` y el `signer` que pase el hook.
 *
 * Mientras no haya implementación real, se devuelven datos mock y las escrituras simulan éxito.
 */

import {
  MOCK_STAKING_PORTFOLIO,
  MOCK_ACTIVE_STAKES,
  MOCK_UNSTAKE_SUMMARY,
  MOCK_UNSTAKE_ITEMS,
  MOCK_STAKE_DETAIL,
} from './stakingMockData';

const STAKING_CONTRACT_ADDRESS = process.env.REACT_APP_STAKING_CONTRACT_ADDRESS || '';

export function getStakingContractAddress() {
  return STAKING_CONTRACT_ADDRESS;
}

export function isStakingContractAddressConfigured() {
  return /^0x[a-fA-F0-9]{40}$/.test(STAKING_CONTRACT_ADDRESS);
}

const delay = (ms) => new Promise((r) => setTimeout(r, ms));

function cloneDashboard() {
  return {
    portfolio: { ...MOCK_STAKING_PORTFOLIO },
    activeStakes: MOCK_ACTIVE_STAKES.map((s) => ({ ...s })),
    unstakeSummary: { ...MOCK_UNSTAKE_SUMMARY },
    unstakeItems: MOCK_UNSTAKE_ITEMS.map((u) => ({ ...u })),
  };
}

// -----------------------------------------------------------------------------
// LECTURAS — IMPLEMENTAR: `ethers.Contract(address, abi, providerOrSigner)`
// -----------------------------------------------------------------------------

async function readDashboardFromContract(_signerOrProvider) {
  // IMPLEMENTAR: pool TVL, stakes del usuario, cola un-stake / claims.
  // Devolver el mismo shape que `cloneDashboard()`.
  console.warn(
    '[staking] readDashboardFromContract() sin implementar; usando mock. Ver stakingContractBridge.js'
  );
  return cloneDashboard();
}

export async function fetchStakingDashboard(signerOrProvider = null) {
  await delay(80);
  if (!isStakingContractAddressConfigured()) {
    return cloneDashboard();
  }
  return readDashboardFromContract(signerOrProvider);
}

export async function fetchStakeDetail(stakeId, signerOrProvider = null) {
  await delay(60);
  if (!isStakingContractAddressConfigured()) {
    return { ...MOCK_STAKE_DETAIL, id: stakeId };
  }
  // IMPLEMENTAR: leer posición / evento por id
  console.warn('[staking] fetchStakeDetail: sin implementar; usando mock.');
  return { ...MOCK_STAKE_DETAIL, id: stakeId };
}

// -----------------------------------------------------------------------------
// ESCRITURAS — IMPLEMENTAR: transacciones con `signer`
// amountHuman: string decimal p.ej. "100.50" (convertir a wei/unidades del token en el contrato)
// -----------------------------------------------------------------------------

async function stakeOnChain(_signer, _amountHuman) {
  // IMPLEMENTAR: ethers.Contract(STAKING_CONTRACT_ADDRESS, abi, signer)
  // await token.approve(...) si aplica; const tx = await stakingContract.stake(...)
  // const receipt = await tx.wait(); return receipt.transactionHash;
  void _signer;
  void _amountHuman;
  throw new Error('IMPLEMENT stakeOnChain() in stakingContractBridge.js');
}

export async function executeStake(signer, amountHuman) {
  await delay(400);
  if (!isStakingContractAddressConfigured()) {
    return { ok: true, txHash: null };
  }
  if (!signer) {
    return { ok: false, error: 'WALLET_REQUIRED' };
  }
  try {
    const txHash = await stakeOnChain(signer, amountHuman);
    return { ok: true, txHash };
  } catch (e) {
    return { ok: false, error: e?.message || 'STAKE_FAILED' };
  }
}

async function unstakeRequestOnChain(_signer, _payload) {
  void _signer;
  void _payload;
  throw new Error('IMPLEMENT unstakeRequestOnChain() in stakingContractBridge.js');
}

export async function executeUnstakeRequest(signer, payload) {
  await delay(400);
  if (!isStakingContractAddressConfigured()) {
    return { ok: true, txHash: null };
  }
  if (!signer) {
    return { ok: false, error: 'WALLET_REQUIRED' };
  }
  try {
    const txHash = await unstakeRequestOnChain(signer, payload);
    return { ok: true, txHash };
  } catch (e) {
    return { ok: false, error: e?.message || 'UNSTAKE_REQUEST_FAILED' };
  }
}

async function claimOnChain(_signer, _payload) {
  void _signer;
  void _payload;
  throw new Error('IMPLEMENT claimOnChain() in stakingContractBridge.js');
}

export async function executeClaim(signer, payload) {
  await delay(400);
  if (!isStakingContractAddressConfigured()) {
    return { ok: true, txHash: null };
  }
  if (!signer) {
    return { ok: false, error: 'WALLET_REQUIRED' };
  }
  try {
    const txHash = await claimOnChain(signer, payload);
    return { ok: true, txHash };
  } catch (e) {
    return { ok: false, error: e?.message || 'CLAIM_FAILED' };
  }
}

async function claimAllOnChain(_signer) {
  void _signer;
  throw new Error('IMPLEMENT claimAllOnChain() in stakingContractBridge.js');
}

export async function executeClaimAll(signer) {
  await delay(400);
  if (!isStakingContractAddressConfigured()) {
    return { ok: true, txHash: null };
  }
  if (!signer) {
    return { ok: false, error: 'WALLET_REQUIRED' };
  }
  try {
    const txHash = await claimAllOnChain(signer);
    return { ok: true, txHash };
  } catch (e) {
    return { ok: false, error: e?.message || 'CLAIM_ALL_FAILED' };
  }
}

async function cancelUnstakeOnChain(_signer, _requestId) {
  void _signer;
  void _requestId;
  throw new Error('IMPLEMENT cancelUnstakeOnChain() in stakingContractBridge.js');
}

export async function executeCancelUnstakeRequest(signer, requestId) {
  await delay(400);
  if (!isStakingContractAddressConfigured()) {
    return { ok: true, txHash: null };
  }
  if (!signer) {
    return { ok: false, error: 'WALLET_REQUIRED' };
  }
  try {
    const txHash = await cancelUnstakeOnChain(signer, requestId);
    return { ok: true, txHash };
  } catch (e) {
    return { ok: false, error: e?.message || 'CANCEL_FAILED' };
  }
}
